import React, { Fragment, useEffect } from "react";
import styled from "styled-components";
import * as Price from "@sellout/utils/.dist/price";
import { Colors, Loader, LoaderSizes } from "@sellout/ui";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
} from "./ScrollableTable";
import UserImage from "@sellout/ui/build/components/UserImage";
import useNavigateToCustomerDetails from "../hooks/useNavigateToCustomerDetails.hook";

import { PaginationTypes } from "@sellout/models/.dist/interfaces/IPagination";
import { useDispatch } from "react-redux";
import * as CustomerActions from "../redux/actions/customer.actions";
import { ContactsScreenEnum } from "@sellout/models/.dist/enums/ContactsScreenEnum";

const NoContentHead = styled.div`
  font-weight: 600;
  font-size: 1.8rem;
  color: ${Colors.Grey1};
  margin-bottom: 5px;
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.White};
  border-radius: 0;
  // const eventState = useSelector((state: BackstageState) => state.event);
  // const { eventId, eventsCache } = eventState;
  padding: 10px 0px;
`;

const TableHeader: React.FC = () => (
  <ScrollTableHeader>
    <ScrollTableHeaderCell flex="1">Name</ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell flex="1">Email</ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell flex="1">Phone</ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell flex="1">Customer</ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell flex="1">Events</ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell flex="1">LTV</ScrollTableHeaderCell>
  </ScrollTableHeader>
);

type TableRowProps = {
  customers: any;
  screenType: string;
};

const TableRows: React.FC<TableRowProps> = ({ customers,screenType }) => {
  const navigateToCustomerDetails = useNavigateToCustomerDetails();
  const dispatch = useDispatch();
  return customers.map((profile: any, index: number) => {
    const {
      userId,
      user,
      firstName: fallbackFirstName,
      lastName: fallbackLastName,
      email: fallbackEmail,
      imageUrl,
      metrics,
    } = profile;

    const customerOnUser = screenType===ContactsScreenEnum.ShowContactsOnly ||screenType===ContactsScreenEnum.ShowContactsAndCustomer; 
      const customerOnUserId = !!userId

    const firstName = customerOnUser ?  fallbackFirstName:user?.firstName;
    const lastName = customerOnUser ? fallbackLastName : user?.lastName ;
    const email = customerOnUser ? fallbackEmail  :user?.email;

    const phoneNumber =user?.phoneNumber;

    const onClick = (event) => {
      dispatch(CustomerActions.userContact({}));
      let selectedUser = {};
      if (!userId || (userId && profile?.metrics?.length <= 0)) {
        // If the userId is not set or if there are no metrics, we assume it's a contact
        if(profile?._id === event) {
        selectedUser = profile;
        dispatch(CustomerActions.userContact(selectedUser));
        }
      }
      const toCustomer = userId && profile?.metrics?.length <= 0 ?"":userId
      navigateToCustomerDetails(toCustomer);
    };
    
    return (
      <ScrollTableBodyRow
        key={index}
        height="40px"
        onClick={(e) => onClick(profile._id)}
      >
        <ScrollTableBodyCell flex="1">
          <UserImage
            height="25px"
            size="1rem"
            imageUrl={imageUrl}
            firstName={firstName}
            lastName={lastName}
            margin="0 10px 0 0"
          />
          <div>{`${firstName} ${lastName}`}</div>
        </ScrollTableBodyCell>
        <ScrollTableSpace />
        <ScrollTableBodyCell flex="1">{email}</ScrollTableBodyCell>
        <ScrollTableSpace />
        <ScrollTableBodyCell flex="1">{phoneNumber}</ScrollTableBodyCell>
        <ScrollTableSpace />
        <ScrollTableBodyCell flex="1">
        {customerOnUserId ? "Yes" : "No"}
        </ScrollTableBodyCell>
        <ScrollTableSpace />
        <ScrollTableBodyCell flex="1">
        {customerOnUserId ?  metrics?.[0]?.eventIds?.length || 0 : 0}
        </ScrollTableBodyCell>
        <ScrollTableSpace />
        <ScrollTableBodyCell flex="1">
          $
          {Price.output(
            customerOnUserId ?metrics?.[0]?.lifeTimeValue || 0 :0,
            true
          )}
        </ScrollTableBodyCell>
      </ScrollTableBodyRow>
    );
  });
};

type CustomerTableProps = {
  customers: any;
  fetchMore?: Function;
  paginationType?: PaginationTypes;
  screenType?: string;
};
enum NoContentMessages {
  NoContacts = "No contacts yet",
  NoCustomers = "No customers yet",
}
const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  fetchMore,
  paginationType,
  screenType = "",
}) => {
  const scrollContainer = React.useRef<any>(null);
  const [length, setLength] = React.useState(0);
  useEffect(() => {
    setLength(customers.length);
  }, [customers.length]);

  /** Render */
  if (customers?.length <= 0) {
    const noContentMessage =
      screenType === ContactsScreenEnum.ShowContactsOnly
      ? NoContentMessages.NoContacts 
      : NoContentMessages.NoCustomers; 

    return (
      <ScrollTable>
        <TableHeader />
        <ScrollTableBody>
          <ScrollTableNoContent>
            <NoContentHead>{noContentMessage}</NoContentHead>
          </ScrollTableNoContent>
        </ScrollTableBody>
      </ScrollTable>
    );
  }

  return (
    <ScrollTable
      fetchMore={fetchMore}
      paginationType={paginationType}
      updateQuery={(prev: any, { fetchMoreResult }: any) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          userProfilesAdmin: [
            ...prev.userProfilesAdmin,
            ...fetchMoreResult.userProfilesAdmin,
          ],
        };
      }}
      scrollContainer={scrollContainer}
    >
      {(loading: boolean) => {
        return (
          <Fragment>
            <TableHeader />
            <ScrollTableBody ref={scrollContainer}>
              <TableRows customers={customers} screenType={screenType} />
              {loading && (
                <LoaderContainer>
                  <Loader size={LoaderSizes.Large} color={Colors.Orange} />
                </LoaderContainer>
              )}
            </ScrollTableBody>
          </Fragment>
        );
      }}
    </ScrollTable>
  );
};

export default CustomerTable;
